import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { TRACK_START, TRACK_SWEEP, angleForScore, describeArc, polar } from '../utils/arc';
const BANDS = [{
  min: 70,
  label: 'Healthy',
  ring: '#7D9B76',
  ringOnDark: '#A9C0A1'
}, {
  min: 50,
  label: 'Watch',
  ring: '#D9A441',
  ringOnDark: '#E8C98A'
}, {
  min: 0,
  label: 'Intervene',
  ring: '#C1654A',
  ringOnDark: '#DDA893'
}];
const EASE = [0.23, 1, 0.32, 1] as const;
interface HealthDialProps {
  score: number;
  /** Last period's score. Enables the delta readout and the memory tick. */
  previous?: number;
  size?: number;
  onDark?: boolean;
  /** Draw the arc on first paint, so arc length reads as the measurement it is. */
  animate?: boolean;
  /** Seconds. Staggers a column of dials. */
  delay?: number;
}

/**
 * Arc length is the score, colour is the band, and the inner tick carries last
 * period's position - two encodings that always agree, plus memory.
 */
export function HealthDial({
  score,
  previous,
  size = 44,
  onDark = false,
  animate = false,
  delay = 0
}: HealthDialProps) {
  const reduceMotion = useReducedMotion();
  const play = animate && !reduceMotion;
  const value = Math.max(0, Math.min(100, score));
  const band = BANDS.find(entry => value >= entry.min) ?? BANDS[2];
  const stroke = size < 34 ? 15 : size < 64 ? 12 : 10;
  const radius = 50 - stroke / 2 - 2;
  const track = describeArc(50, 50, radius, TRACK_START, TRACK_START + TRACK_SWEEP);
  const fill = describeArc(50, 50, radius, TRACK_START, angleForScore(value));
  const hasPrior = previous !== undefined;
  const delta = hasPrior ? Math.round(value - Math.max(0, Math.min(100, previous))) : 0;
  const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'steady';
  const ring = onDark ? band.ringOnDark : band.ring;
  const showTick = hasPrior && delta !== 0;
  const tickAngle = angleForScore(previous ?? value);
  const tickInner = polar(50, 50, radius - stroke / 2, tickAngle);
  const tickOuter = polar(50, 50, radius + stroke / 2, tickAngle);
  const tickColour = onDark ? 'rgba(20,20,18,0.5)' : 'rgba(255,255,255,0.85)';
  const label = `Health ${value} of 100, ${band.label}${hasPrior ? `, ${dir === 'steady' ? 'steady' : `${dir} ${Math.abs(delta)}`}` : ''}`;
  return <span role="img" aria-label={label} className="relative inline-flex shrink-0 items-center justify-center" style={{
    width: size,
    height: size
  }} data-id="element-17" data-mp="1o28smgg423n2">
      <svg viewBox="0 0 100 100" width={size} height={size} className="block" aria-hidden="true" data-id="element-18" data-mp="2gpcsy849dtsi">
        <path d={track} fill="none" stroke={onDark ? 'rgba(255,255,255,0.14)' : '#E4E2DA'} strokeWidth={stroke} strokeLinecap="round" data-id="element-19" data-mp="2bscn9ybbmt1x" />

        {fill && (play ? <motion.path d={fill} fill="none" stroke={ring} strokeWidth={stroke} strokeLinecap="round" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 0.36,
        ease: EASE,
        delay
      }} data-id="element-20" data-mp="21zfbeb0u42ca" /> : <path d={fill} fill="none" stroke={ring} strokeWidth={stroke} strokeLinecap="round" data-id="element-21" data-mp="36zn04m25fhzj" />)}

        {showTick && (play ? <motion.line x1={tickInner.x} y1={tickInner.y} x2={tickOuter.x} y2={tickOuter.y} stroke={tickColour} strokeWidth={2.6} strokeLinecap="round" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.16,
        ease: EASE,
        delay: delay + 0.32
      }} data-id="element-22" data-mp="3c1ugroxxbm87" /> : <line x1={tickInner.x} y1={tickInner.y} x2={tickOuter.x} y2={tickOuter.y} stroke={tickColour} strokeWidth={2.6} strokeLinecap="round" data-id="element-23" data-mp="3k7tzvww5sjo" />)}
      </svg>

      <span className="pointer-events-none absolute inset-0 flex items-center justify-center" data-id="element-24" data-mp="1a8zf0vof1fse">
        <span className="font-semibold leading-none tabular-nums" style={{
        fontSize: size * 0.36,
        color: onDark ? '#F5F5F0' : '#1C1C18'
      }} data-id="element-25" data-mp="15a5zkxpkjepl">
          {value}
        </span>
      </span>
    </span>;
}